import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

// Why we said no.
//
// ─── WHY THIS DIALOG EXISTS ─────────────────────────────────────────────────
//
// Rejecting used to be a single click that sent `remarks: ""`. The server then
// stored the placeholder "Candidate rejected", which in the audit trail is
// indistinguishable from a reason somebody actually typed — worse than a blank,
// because a blank is visibly missing.
//
// The server now refuses a rejection without a reason. This is the other half:
// collecting one, rather than letting the click fail with an error the operator
// cannot act on from where they are standing.
//
// ─── WHY IT ALSO GUARDS THE CLICK ───────────────────────────────────────────
//
// It doubles as the confirmation the reject button never had. Reject sits one
// icon away from delete on a dense row, and it was immediate — on the bulk path
// it was immediate for up to two hundred people at once. Typing a reason is a
// deliberate act, which is the right shape for this decision.
//
// ─── THE QUICK REASONS ──────────────────────────────────────────────────────
//
// Chips fill the box, they do not submit. They exist because the common reasons
// are genuinely common and typing them out is what makes people resent the
// requirement — and a resented requirement gets "asdf" typed into it. Every
// chip leaves the text editable, so "Not enough experience" can become "Not
// enough experience — only 1 year of React, we need 4+" with no retyping.
//
// They are NOT a closed list, deliberately. A dropdown of rejection reasons
// becomes the only vocabulary anybody uses, and the interesting reason is
// always the one not on the list.

const QUICK_REASONS = [
  "Not enough experience",
  "Skills do not match the role",
  "Salary expectation above range",
  "Notice period too long",
  "Location / relocation",
  "Better candidates available",
  "Did not attend the interview",
  "Withdrew / not interested",
  "Role closed",
];

// Matches the server's floor (utils/hiringActionNote.MIN_REASON). Kept in step
// deliberately: a client that accepts less produces a request the server
// refuses, and the operator sees a failure they thought they had satisfied.
const MIN_REASON = 3;

export default function RejectReasonDialog({
  open,
  onClose,
  onConfirm,
  // One name, or a count for the bulk path. Both shapes so the copy can be
  // specific — "Reject Asha Rao" reads very differently from "Reject 14
  // candidates", and the second deserves more hesitation.
  candidateName,
  count = 1,
  busy = false,
}) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const many = count > 1;
  const trimmed = reason.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < MIN_REASON;

  // Cleared on every open. A reason typed for one candidate must never be
  // sitting in the box for the next one — this dialog is reused, and a stale
  // reason would be attributed to somebody it was not written about.
  useEffect(() => {
    if (!open) return;
    setReason("");
    setError("");
    // Focused after the dialog has finished mounting, so the caret lands in the
    // box rather than the requirement being something to hunt for.
    const t = setTimeout(() => inputRef.current?.focus(), 120);
    return () => clearTimeout(t);
  }, [open]);

  const submit = async () => {
    if (!trimmed || tooShort) {
      setError(
        !trimmed
          ? "Please say why. This is the only record of the decision."
          : "That is too short to mean anything. Please say briefly why.",
      );
      inputRef.current?.focus();
      return;
    }
    setError("");
    try {
      await onConfirm(trimmed);
    } catch (e) {
      // Shown here rather than in the caller's toast: the dialog is still on
      // screen and holds the text the operator typed. Closing it and reporting
      // elsewhere would lose the reason and make them write it again.
      setError(
        e?.response?.data?.statusMessage ||
          e?.message ||
          "Could not reject. Nothing was changed.",
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
      onClick={(e) => e.stopPropagation()}
    >
      <DialogContent sx={{ p: 3 }}>
        <Typography sx={{ fontSize: 17, fontWeight: 700, color: "#101828" }}>
          {many ? `Reject ${count} candidates` : "Reject this candidate"}
        </Typography>

        <Typography sx={{ fontSize: 13.5, color: "#475467", mt: 0.75, lineHeight: 1.6 }}>
          {many ? (
            <>
              The same reason is recorded against all {count}, along with your
              name. They stay on the job in the Rejected list and can be put
              back.
            </>
          ) : (
            <>
              {candidateName ? <strong>{candidateName}</strong> : "This candidate"}{" "}
              moves to the Rejected list. Nothing is deleted, and it can be
              undone — but the reason and your name are recorded either way.
            </>
          )}
        </Typography>

        <TextField
          inputRef={inputRef}
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            if (error) setError("");
          }}
          onKeyDown={(e) => {
            // Ctrl/Cmd+Enter submits. Plain Enter inserts a newline, because
            // this is a multiline box and somebody halfway through a sentence
            // pressing Enter should not have rejected somebody.
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              submit();
            }
          }}
          label="Why are you rejecting?"
          placeholder="e.g. Only 1 year of React; the role needs 4+"
          multiline
          minRows={2}
          maxRows={5}
          fullWidth
          required
          disabled={busy}
          error={Boolean(error) || tooShort}
          helperText={
            error ||
            (tooShort
              ? "A little more than that."
              : "Recorded on the candidate's history against your name. Ctrl+Enter to save.")
          }
          sx={{ mt: 2.5, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          // 1000 matches applications.remarks and the server's cap, so the box
          // cannot accept text the server would silently truncate.
          inputProps={{ maxLength: 1000 }}
        />

        <Typography
          sx={{ fontSize: 11.5, fontWeight: 700, color: "#667085", mt: 2, mb: 1 }}
        >
          COMMON REASONS — TAP TO FILL, THEN EDIT
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          {QUICK_REASONS.map((r) => (
            <Chip
              key={r}
              label={r}
              size="small"
              onClick={() => {
                setReason(r);
                setError("");
                // Focus returns to the box with the caret at the end, so the
                // chip is a starting point rather than a final answer.
                setTimeout(() => {
                  const el = inputRef.current;
                  if (!el) return;
                  el.focus();
                  const end = el.value.length;
                  el.setSelectionRange(end, end);
                }, 0);
              }}
              disabled={busy}
              sx={{
                fontSize: 12,
                cursor: "pointer",
                bgcolor: "#f9fafb",
                border: "1px solid #e4e7ec",
                "&:hover": { bgcolor: "#fef3f2", borderColor: "#fda29b" },
              }}
            />
          ))}
        </Box>

        {many && (
          <Alert severity="warning" sx={{ mt: 2.5, borderRadius: 2, fontSize: 12.5 }}>
            {count} candidates at once. Anyone already rejected is left alone.
          </Alert>
        )}

        <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 3 }}>
          <Button
            onClick={onClose}
            disabled={busy}
            sx={{ textTransform: "none", fontWeight: 600, color: "#475467" }}
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            variant="contained"
            // Disabled until there is something to record. The requirement is
            // visible in the button rather than only discovered on submit.
            disabled={busy || !trimmed || tooShort}
            startIcon={busy ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : null}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              bgcolor: "#b42318",
              "&:hover": { bgcolor: "#912018" },
            }}
          >
            {busy
              ? "Rejecting…"
              : many
                ? `Reject ${count} with this reason`
                : "Reject with this reason"}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
